import addInfoBarMessage from './addInfoBarMessage';
import clearUpgradeTimer from './clearUpgradeTimer';
import removeInfoBarMessage from './removeInfoBarMessage';
import { saveWithAction } from './trySaveMessage';
import { findComposePlugins } from '../plugins/ComposePlugins';
import createAppendOnSendBlock from '../utils/createAppendOnSendBlock';
import buildReplyForwardMessageBody from '../utils/buildReplyForwardMessageBody';
import { isPublicFolderComposeViewState } from '../utils/createPublicFolderComposeViewState';
import getFromViewState from '../utils/getFromViewState';
import { infoBarAddHandler, infoBarRemoveHandler } from '../utils/infoBarHandlers';
import shouldAlwaysShowFrom from '../utils/shouldAlwaysShowFrom';
import { logUsage } from 'owa-analytics';
import { lazyInitializeAttachments } from 'owa-attachment-well-data';
import { getUserMailboxInfo } from 'owa-client-ids';
import isMeetingRequest from 'owa-meeting-message/lib/utils/isMeetingRequest';
import Message from 'owa-service/lib/contract/Message';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import convertInlineCssForHtml from '../utils/convertInlineCssForHtml';
import { action } from 'satcheljs/lib/legacy';
import ComposeViewState, {
    AsyncSendState,
    ComposeOperation,
} from '../store/schema/ComposeViewState';

const UPGRADE_TIMEOUT = 5000;
const UPGRADE_CHANGE_SOURCE = 'UpgradeCompose';

const UpgradeCompletedDatapoint = 'UpgradeComposeCompleted';

export default action('upgradeCompose')(function upgradeCompose(
    viewState: ComposeViewState,
    createMessage: Promise<Message>
) {
    logUsage('UpgradeCompose');

    let upgradeCompleted = false;
    viewState.upgradeTimer = setTimeout(() => {
        if (!upgradeCompleted) {
            // Add "quoted body will be loaded" infobar if quoted body is not loaded in time limit
            showUpgradeMessageInfobar(viewState);
            logUsage('UpgradeComposeTimedOut');
        }
    }, UPGRADE_TIMEOUT);

    // Saving action is active until upgrade is complete, so that createAttachments is
    // paused on waitForActiveSaving if the itemId has not been retrieved yet. This
    // prevents double draft and inline images being created with the wrong itemId (VSO 33271)
    saveWithAction(
        viewState,
        createMessage.then(message => {
            if (message) {
                const itemId = message.ItemId;
                viewState.itemId = itemId;

                const quotedBody = message.Body.Value;
                viewState.quotedBody = quotedBody;

                const attachments = message.Attachments;
                if (attachments) {
                    lazyInitializeAttachments.importAndExecute(
                        attachments,
                        getUserMailboxInfo(),
                        viewState.attachmentWell,
                        true /* forceUpdateAttachmentsInStore */,
                        true /* isInitializationForCompose */,
                        null,
                        itemId,
                        infoBarAddHandler(viewState),
                        infoBarRemoveHandler(viewState)
                    );
                }

                const isForward = viewState.operation === ComposeOperation.Forward;
                if (isForward) {
                    const meetingRequestItem = message.ItemClass
                        ? isMeetingRequest(message.ItemClass) != null
                            ? message
                            : null
                        : null;
                    viewState.meetingRequestItem = meetingRequestItem;

                    if (shouldAlwaysShowFrom() && message.From) {
                        viewState.fromViewState = getFromViewState(
                            message.From.Mailbox,
                            meetingRequestItem,
                            viewState.referenceItemId,
                            isPublicFolderComposeViewState(viewState)
                        );
                    }
                }

                // Upgrade compose if message isn't already sending
                // Otherwise, skip upgrade but alert the user the quoted body will be loaded
                if (viewState.asyncSendState == AsyncSendState.None && !viewState.isSending) {
                    if (quotedBody) {
                        insertQuotedBody(viewState, isForward);
                        viewState.useSmartResponse = false; // Quoted body is loaded, no need to use smart response now
                        removeInfoBarMessage(viewState, 'warningNoQuotedBody');
                        logUsage(UpgradeCompletedDatapoint, [true]);
                    }
                } else {
                    // Add "quoted body will be loaded" infobar if upgrade should be skipped
                    showUpgradeMessageInfobar(viewState);
                    logUsage(UpgradeCompletedDatapoint, [false, 'messageAlreadySending']);
                }
            } else {
                // Add "original message can't be shown right now" infobar if quoted body is not loaded for smart response in full compose
                showFailureToShowOriginalMessageInfobar(viewState);
                logUsage(UpgradeCompletedDatapoint, [false, 'quotedBodyNotLoaded']);
            }

            upgradeCompleted = true;
            clearUpgradeTimer(viewState);

            // NOTE: createReplyFromReplyItem currently returns null in the case of failure
            // If this is changed, showFailureToShowOriginalMessageInfobar should be invoked in a catch
        })
    );
});

function insertQuotedBody(viewState: ComposeViewState, isForward: boolean) {
    const plugins = findComposePlugins(viewState);
    if (plugins) {
        const contentOperationPlugin = plugins.contentOperationPlugin;
        if (contentOperationPlugin) {
            const formattedQuotedBody = formatQuotedBody(viewState, isForward);
            contentOperationPlugin.insertContentIntoEditor(
                formattedQuotedBody,
                UPGRADE_CHANGE_SOURCE,
                true /* belowExistingContent */,
                !isForward /* setFocusToEditor */
            );
        }
    }
}

// There should be a new line above the quoted body (VSO 40399)
// This is already handled when:
// 1. The user has auto-signature enabled (see createSignatureBlock)
// 2. Replying in item view (see createReply > createReplyItem)
function formatQuotedBody(viewState: ComposeViewState, isForward: boolean) {
    const userConfig = getUserConfiguration();
    const isAutoSignature =
        userConfig && userConfig.UserOptions && userConfig.UserOptions.AutoAddSignatureOnReply;

    if (isAutoSignature || !isForward) {
        let quotedBody = convertInlineCssForHtml(viewState.quotedBody, viewState.bodyType);
        return createAppendOnSendBlock(viewState.bodyType) + quotedBody;
    }

    return buildReplyForwardMessageBody(viewState.quotedBody, viewState.bodyType);
}

function showUpgradeMessageInfobar(viewState: ComposeViewState) {
    addInfoBarMessage(viewState, 'warningNoQuotedBody');
}

export function showFailureToShowOriginalMessageInfobar(viewState: ComposeViewState) {
    removeInfoBarMessage(viewState, 'warningNoQuotedBody');
    addInfoBarMessage(viewState, 'warningFailureToShowOriginalMessage');
}
